import { AlertTriangle, Clock3 } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { getTradingSessionUrgency } from '../authority';

interface TradingSessionWarningProps {
    remainingMs: number;
    onReauthenticate: () => void;
}

const TradingSessionWarning: React.FC<TradingSessionWarningProps> = ({
    remainingMs,
    onReauthenticate,
}) => {
    const urgency = getTradingSessionUrgency(remainingMs);
    if (urgency === 'none') return null;

    const seconds = Math.max(0, Math.ceil(remainingMs / 1000));
    const message =
        urgency === 'expired'
            ? 'Trading window closed'
            : urgency === 'warning'
                ? 'Trading window closes in 1 minute'
                : `Trading window closes in ${seconds} second${seconds === 1 ? '' : 's'}`;
    const Icon = urgency === 'warning' ? Clock3 : AlertTriangle;

    return (
        <section
            role="status"
            aria-live="polite"
            data-urgency={urgency}
            data-trading-session-warning=""
            // above the z-50 modal panels (deposit, withdraw) and clickable through
            // their pointer lock: a user mid-deposit must still be able to renew the
            // window instead of being signed out when it closes.
            className={cn(
                'pointer-events-auto fixed inset-x-3 top-3 z-[60] mx-auto flex max-w-xl items-center gap-3 rounded-xl border px-3 py-2 shadow-xl backdrop-blur-md',
                urgency === 'warning'
                    ? 'border-amber-400/50 bg-amber-950/90 text-amber-100'
                    : 'border-destructive/60 bg-red-950/95 text-red-100',
            )}
        >
            <Icon size={18} className="shrink-0" />
            <p className="min-w-0 flex-1 text-sm font-semibold">{message}</p>
            <Button
                type="button"
                size="sm"
                variant={urgency === 'warning' ? 'outline' : 'destructive'}
                onClick={onReauthenticate}
            >
                Reauthenticate
            </Button>
        </section>
    );
};

export default TradingSessionWarning;
